import { createSlice } from '@reduxjs/toolkit';
import { 
    fetchTotalAudience, 
    fetchTotalSubscribers, 
    fetchNetSubscribers,
    fetchSubscriberRate,
    fetchAudienceRetention,
    fetchAgeDistribution,
    fetchGenderDistribution,
    fetchTopCountries,
    fetchDeviceDistribution,
    fetchInterestCategories,
    fetchTrafficSources,
    fetchSharesDistribution,
    fetchPremiumViews,
    fetchCardPerformance,
    downloadAgeDistributionCSV,
    downloadGenderDistributionCSV,
    downloadTopCountriesCSV,
    downloadDeviceDistributionCSV,
    downloadInterestCategoriesCSV,
    downloadTrafficSourcesCSV,
    downloadSharesDistributionCSV,
    downloadPremiumViewsCSV,
    downloadCardPerformanceCSV,
} from '../services/operations/audienceInsightApi';

const initialState = {
    totalAudience: {
        data: null,
        loading: true,
        error: null
    },
    totalSubscribers: {
        data: null,
        loading: true,
        error: null
    },
    netSubscribers: {
        data: null,
        loading: true,
        error: null
    },
    subscriberRate: {
        data: null,
        loading: true,
        error: null
    },
    audienceRetention: {
        data: null,
        loading: true,
        error: null
    },
    ageDistribution: {
        data: null,
        loading: true,
        error: null
    },
    genderDistribution: {
        data: null,
        loading: true,
        error: null
    },
    topCountries: {
        data: null,
        loading: true,
        error: null
    },
    deviceDistribution: {
        data: null,
        loading: true,
        error: null
    },
    interestCategories: {
        data: null,
        loading: true,
        error: null
    },
    trafficSources: {
        data: null,
        loading: true,
        error: null
    },
    sharesDistribution: {
        data: null,
        loading: true,
        error: null
    },
    premiumViews: {
        data: null,
        loading: true,
        error: null
    },
    cardPerformance: {
        data: null,
        loading: true,
        error: null
    },
    filters: {
        selectedChannelId: [],
        filterType: 'last_12_months'
    },
    downloads: {
        ageDistribution: { loading: false, error: null },
        genderDistribution: { loading: false, error: null },
        topCountries: { loading: false, error: null },
        deviceDistribution: { loading: false, error: null },
        interestCategories: { loading: false, error: null },
        trafficSources: { loading: false, error: null },
        sharesDistribution: { loading: false, error: null },
        premiumViews: { loading: false, error: null },
        cardPerformance: { loading: false, error: null },
    },
};

const audienceInsightSlice = createSlice({
    name: 'audienceInsight',
    initialState,
    reducers: {
        setAudienceFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearAudienceData(state) {
            // Reset all data but keep loading state as true
            state.totalAudience = { ...initialState.totalAudience, loading: true };
            state.totalSubscribers = { ...initialState.totalSubscribers, loading: true };
            state.netSubscribers = { ...initialState.netSubscribers, loading: true };
            state.subscriberRate = { ...initialState.subscriberRate, loading: true };
            state.audienceRetention = { ...initialState.audienceRetention, loading: true };
            state.ageDistribution = { ...initialState.ageDistribution, loading: true };
            state.deviceDistribution = { ...initialState.deviceDistribution, loading: true };
            state.interestCategories = { ...initialState.interestCategories, loading: true };
            state.trafficSources = { ...initialState.trafficSources, loading: true };
            state.sharesDistribution = { ...initialState.sharesDistribution, loading: true };
            state.premiumViews = { ...initialState.premiumViews, loading: true };
            state.cardPerformance = { ...initialState.cardPerformance, loading: true };
        }
    },
    extraReducers: (builder) => {
        builder
            // Total Audience API cases
            .addCase(fetchTotalAudience.pending, (state) => {
                state.totalAudience.loading = true;
                state.totalAudience.error = null;
            })
            .addCase(fetchTotalAudience.fulfilled, (state, action) => {
                state.totalAudience.loading = false;
                state.totalAudience.data = action.payload;
                state.totalAudience.error = null;
            })
            .addCase(fetchTotalAudience.rejected, (state, action) => {
                state.totalAudience.loading = false;
                state.totalAudience.error = action.payload;
            })
            
            // Total Subscribers API cases
            .addCase(fetchTotalSubscribers.pending, (state) => {
                state.totalSubscribers.loading = true;
                state.totalSubscribers.error = null;
            })
            .addCase(fetchTotalSubscribers.fulfilled, (state, action) => {
                state.totalSubscribers.loading = false;
                state.totalSubscribers.data = action.payload;
                state.totalSubscribers.error = null;
            })
            .addCase(fetchTotalSubscribers.rejected, (state, action) => {
                state.totalSubscribers.loading = false;
                state.totalSubscribers.error = action.payload;
            })
            
            // Net Subscribers API cases
            .addCase(fetchNetSubscribers.pending, (state) => {
                state.netSubscribers.loading = true;
                state.netSubscribers.error = null;
            })
            .addCase(fetchNetSubscribers.fulfilled, (state, action) => {
                state.netSubscribers.loading = false;
                state.netSubscribers.data = action.payload;
                state.netSubscribers.error = null;
            })
            .addCase(fetchNetSubscribers.rejected, (state, action) => {
                state.netSubscribers.loading = false;
                state.netSubscribers.error = action.payload;
            })
            
            // Subscriber Rate API cases
            .addCase(fetchSubscriberRate.pending, (state) => {
                state.subscriberRate.loading = true;
                state.subscriberRate.error = null;
            })
            .addCase(fetchSubscriberRate.fulfilled, (state, action) => {
                state.subscriberRate.loading = false;
                state.subscriberRate.data = action.payload;
                state.subscriberRate.error = null;
            })
            .addCase(fetchSubscriberRate.rejected, (state, action) => {
                state.subscriberRate.loading = false;
                state.subscriberRate.error = action.payload;
            })
            
            // Audience Retention API cases
            .addCase(fetchAudienceRetention.pending, (state) => {
                state.audienceRetention.loading = true;
                state.audienceRetention.error = null;
            })
            .addCase(fetchAudienceRetention.fulfilled, (state, action) => {
                state.audienceRetention.loading = false;
                state.audienceRetention.data = action.payload;
                state.audienceRetention.error = null;
            })
            .addCase(fetchAudienceRetention.rejected, (state, action) => {
                state.audienceRetention.loading = false;
                state.audienceRetention.error = action.payload || 'Failed to fetch audience retention data';
            })
            
            // Age Distribution API cases
            .addCase(fetchAgeDistribution.pending, (state) => {
                state.ageDistribution.loading = true;
                state.ageDistribution.error = null;
            })
            .addCase(fetchAgeDistribution.fulfilled, (state, action) => {
                state.ageDistribution.loading = false;
                state.ageDistribution.data = action.payload;
                state.ageDistribution.error = null;
            })
            .addCase(fetchAgeDistribution.rejected, (state, action) => {
                state.ageDistribution.loading = false;
                state.ageDistribution.error = action.payload || 'Failed to fetch age distribution data';
            })
            
            // Gender Distribution API cases
            .addCase(fetchGenderDistribution.pending, (state) => {
                state.genderDistribution.loading = true;
                state.genderDistribution.error = null;
            })
            .addCase(fetchGenderDistribution.fulfilled, (state, action) => {
                state.genderDistribution.loading = false;
                state.genderDistribution.data = action.payload;
                state.genderDistribution.error = null;
            })
            .addCase(fetchGenderDistribution.rejected, (state, action) => {
                state.genderDistribution.loading = false;
                state.genderDistribution.error = action.payload || 'Failed to fetch gender distribution data';
            })
            
            // Top Countries API cases
            .addCase(fetchTopCountries.pending, (state) => {
                state.topCountries.loading = true;
                state.topCountries.error = null;
            })
            .addCase(fetchTopCountries.fulfilled, (state, action) => {
                state.topCountries.loading = false;
                state.topCountries.data = action.payload;
                state.topCountries.error = null;
            })
            .addCase(fetchTopCountries.rejected, (state, action) => {
                state.topCountries.loading = false;
                state.topCountries.error = action.payload || 'Failed to fetch top countries data';
            })
            // Device Distribution reducers
            .addCase(fetchDeviceDistribution.pending, (state) => {
                state.deviceDistribution.loading = true;
                state.deviceDistribution.error = null;
            })
            .addCase(fetchDeviceDistribution.fulfilled, (state, action) => {
                state.deviceDistribution.loading = false;
                state.deviceDistribution.data = action.payload.devices || [];
                state.deviceDistribution.error = null;
            })
            .addCase(fetchDeviceDistribution.rejected, (state, action) => {
                state.deviceDistribution.loading = false;
                state.deviceDistribution.error = action.payload || 'Failed to fetch device distribution data';
            })
            // Interest Categories reducers
            .addCase(fetchInterestCategories.pending, (state) => {
                state.interestCategories.loading = true;
                state.interestCategories.error = null;
            })
            .addCase(fetchInterestCategories.fulfilled, (state, action) => {
                state.interestCategories.loading = false;
                state.interestCategories.data = action.payload.categories || [];
                state.interestCategories.error = null;
            })
            .addCase(fetchInterestCategories.rejected, (state, action) => {
                state.interestCategories.loading = false;
                state.interestCategories.error = action.payload || 'Failed to fetch interest categories data';
            })
            // Traffic Sources reducers
            .addCase(fetchTrafficSources.pending, (state) => {
                state.trafficSources.loading = true;
                state.trafficSources.error = null;
            })
            .addCase(fetchTrafficSources.fulfilled, (state, action) => {
                state.trafficSources.loading = false;
                state.trafficSources.data = action.payload.traffic_sources || [];
                state.trafficSources.error = null;
            })
            .addCase(fetchTrafficSources.rejected, (state, action) => {
                state.trafficSources.loading = false;
                state.trafficSources.error = action.payload || 'Failed to fetch traffic sources data';
            })
            // Shares Distribution reducers
            .addCase(fetchSharesDistribution.pending, (state) => {
                state.sharesDistribution.loading = true;
                state.sharesDistribution.error = null;
            })
            .addCase(fetchSharesDistribution.fulfilled, (state, action) => {
                state.sharesDistribution.loading = false;
                state.sharesDistribution.data = action.payload.sharing_platforms || [];
                state.sharesDistribution.error = null;
            })
            .addCase(fetchSharesDistribution.rejected, (state, action) => {
                state.sharesDistribution.loading = false;
                state.sharesDistribution.error = action.payload || 'Failed to fetch shares distribution data';
            })
            // Premium Views reducers
            .addCase(fetchPremiumViews.pending, (state) => {
                state.premiumViews.loading = true;
                state.premiumViews.error = null;
            })
            .addCase(fetchPremiumViews.fulfilled, (state, action) => {
                state.premiumViews.loading = false;
                state.premiumViews.data = action.payload.premium_views_by_source || [];
                state.premiumViews.error = null;
            })
            .addCase(fetchPremiumViews.rejected, (state, action) => {
                state.premiumViews.loading = false;
                state.premiumViews.error = action.payload || 'Failed to fetch premium views data';
            })
            // Card Performance reducers
            .addCase(fetchCardPerformance.pending, (state) => {
                state.cardPerformance.loading = true;
                state.cardPerformance.error = null;
            })
            .addCase(fetchCardPerformance.fulfilled, (state, action) => {
                state.cardPerformance.loading = false;
                state.cardPerformance.data = action.payload.card_performance_metrics || [];
                state.cardPerformance.error = null;
            })
            .addCase(fetchCardPerformance.rejected, (state, action) => {
                state.cardPerformance.loading = false;
                state.cardPerformance.error = action.payload || 'Failed to fetch card performance data';
            })
            // Age Distribution CSV Download
            .addCase(downloadAgeDistributionCSV.pending, (state) => {
                state.downloads.ageDistribution.loading = true;
                state.downloads.ageDistribution.error = null;
            })
            .addCase(downloadAgeDistributionCSV.fulfilled, (state) => {
                state.downloads.ageDistribution.loading = false;
                state.downloads.ageDistribution.error = null;
            })
            .addCase(downloadAgeDistributionCSV.rejected, (state, action) => {
                state.downloads.ageDistribution.loading = false;
                state.downloads.ageDistribution.error = action.payload;
            })
            
            // Gender Distribution CSV Download
            .addCase(downloadGenderDistributionCSV.pending, (state) => {
                state.downloads.genderDistribution.loading = true;
                state.downloads.genderDistribution.error = null;
            })
            .addCase(downloadGenderDistributionCSV.fulfilled, (state) => {
                state.downloads.genderDistribution.loading = false;
                state.downloads.genderDistribution.error = null;
            })
            .addCase(downloadGenderDistributionCSV.rejected, (state, action) => {
                state.downloads.genderDistribution.loading = false;
                state.downloads.genderDistribution.error = action.payload;
            })

            // Top Countries CSV Download
            .addCase(downloadTopCountriesCSV.pending, (state) => {
                state.downloads.topCountries.loading = true;
                state.downloads.topCountries.error = null;
            })
            .addCase(downloadTopCountriesCSV.fulfilled, (state) => {
                state.downloads.topCountries.loading = false;
                state.downloads.topCountries.error = null;
            })
            .addCase(downloadTopCountriesCSV.rejected, (state, action) => {
                state.downloads.topCountries.loading = false;
                state.downloads.topCountries.error = action.payload;
            })

            // Device Distribution CSV Download
            .addCase(downloadDeviceDistributionCSV.pending, (state) => {
                state.downloads.deviceDistribution.loading = true;
                state.downloads.deviceDistribution.error = null;
            })
            .addCase(downloadDeviceDistributionCSV.fulfilled, (state) => {
                state.downloads.deviceDistribution.loading = false;
                state.downloads.deviceDistribution.error = null;
            })
            .addCase(downloadDeviceDistributionCSV.rejected, (state, action) => {
                state.downloads.deviceDistribution.loading = false;
                state.downloads.deviceDistribution.error = action.payload;
            })

            // Interests Categories CSV Download
            .addCase(downloadInterestCategoriesCSV.pending, (state) => {
                state.downloads.interestCategories.loading = true;
                state.downloads.interestCategories.error = null;
            })
            .addCase(downloadInterestCategoriesCSV.fulfilled, (state) => {
                state.downloads.interestCategories.loading = false;
                state.downloads.interestCategories.error = null;
            })
            .addCase(downloadInterestCategoriesCSV.rejected, (state, action) => {
                state.downloads.interestCategories.loading = false;
                state.downloads.interestCategories.error = action.payload;
            })

            // Traffic Sources CSV Download
            .addCase(downloadTrafficSourcesCSV.pending, (state) => {
                state.downloads.trafficSources.loading = true;
                state.downloads.trafficSources.error = null;
            })
            .addCase(downloadTrafficSourcesCSV.fulfilled, (state) => {
                state.downloads.trafficSources.loading = false;
                state.downloads.trafficSources.error = null;
            })
            .addCase(downloadTrafficSourcesCSV.rejected, (state, action) => {
                state.downloads.trafficSources.loading = false;
                state.downloads.trafficSources.error = action.payload;
            })

            // Shares Distribution CSV Download
            .addCase(downloadSharesDistributionCSV.pending, (state) => {
                state.downloads.sharesDistribution.loading = true;
                state.downloads.sharesDistribution.error = null;
            })
            .addCase(downloadSharesDistributionCSV.fulfilled, (state) => {
                state.downloads.sharesDistribution.loading = false;
                state.downloads.sharesDistribution.error = null;
            })
            .addCase(downloadSharesDistributionCSV.rejected, (state, action) => {
                state.downloads.sharesDistribution.loading = false;
                state.downloads.sharesDistribution.error = action.payload;
            })

            // Premium Views CSV Download
            .addCase(downloadPremiumViewsCSV.pending, (state) => {
                state.downloads.premiumViews.loading = true;
                state.downloads.premiumViews.error = null;
            })
            .addCase(downloadPremiumViewsCSV.fulfilled, (state) => {
                state.downloads.premiumViews.loading = false;
                state.downloads.premiumViews.error = null;
            })
            .addCase(downloadPremiumViewsCSV.rejected, (state, action) => {
                state.downloads.premiumViews.loading = false;
                state.downloads.premiumViews.error = action.payload;
            })

            // Card Performance CSV Download
            .addCase(downloadCardPerformanceCSV.pending, (state) => {
                state.downloads.cardPerformance.loading = true;
                state.downloads.cardPerformance.error = null;
            })
            .addCase(downloadCardPerformanceCSV.fulfilled, (state) => {
                state.downloads.cardPerformance.loading = false;
                state.downloads.cardPerformance.error = null;
            })
            .addCase(downloadCardPerformanceCSV.rejected, (state, action) => {
                state.downloads.cardPerformance.loading = false;
                state.downloads.cardPerformance.error = action.payload;
            })
            
    }
});

export const { setAudienceFilters, clearAudienceData } = audienceInsightSlice.actions;
export default audienceInsightSlice.reducer;
